import { useTranslator } from '@/hooks/useTranslator';
import { ENV_LABEL } from '@/lib/utils/env';

/**
 * Permanent "this is not the real dashboard" bar for non-production copies,
 * such as the Zeeman test environment.
 *
 * Deliberately not dismissible, and deliberately not the same component as
 * `OldDashboardBanner` even though the two share the row above the shell.
 * That one belongs to the v1 opt-out — it carries a remembered dismissal, the
 * user-menu entry that brings it back, and the `frontend_v2_opt_out_*`
 * adoption metrics — and it is meant to be deleted along with v1. A warning
 * users can permanently hide, or that disappears when the old dashboard does,
 * is not a warning.
 */
export function EnvironmentBanner() {
  const { getTranslation: t } = useTranslator({ page: 'shared' });

  // Unset everywhere except the environments that want the bar, which is how
  // production opts out without knowing this component exists.
  if (!ENV_LABEL) return null;

  return (
    // `warning`, not the brand `primary`: the token table defines this one as
    // "caution needed", and the reader does need to act on it — nothing on the
    // page can be trusted as real. In brand teal it would read as a feature
    // announcement instead. Solid rather than the tinted `Alert` recipe
    // (`bg-warning/10`) because this is a full-bleed shell bar like the opt-out
    // row above it, and a 10% tint across 36px reads as decoration — which is
    // how a permanent notice stops being seen. `warning-foreground` is the
    // theme's documented partner for this background in both light and dark.
    <div className='flex h-9 w-full shrink-0 items-center justify-center gap-2 bg-warning px-4 text-warning-foreground'>
      <span className='rounded-sm bg-warning-foreground/15 px-1.5 py-0.5 text-xs font-semibold uppercase'>
        {ENV_LABEL}
      </span>
      <span className='truncate text-xs'>{t('environmentBanner.notice')}</span>
    </div>
  );
}
